import React, { useState, useEffect, useRef } from 'react';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import { Col, Button, InputGroup, Row, Modal, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
// custom hooks
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
//icons
import { FaXmark } from 'react-icons/fa6';
import { BsBook, BsPerson, BsBuilding, BsHash } from 'react-icons/bs';
// components
import SearchBar from './SearchBar';
import './SearchBarDesktop.scss';

const initialSearchValues = {
  value: '',
  searchType: 'titulo'
};

const SearchOptionsModal = ({ 
  show, 
  onHide, 
  searchValue, 
  currentSearchType, 
  onSelectOption, 
  onSubmit, 
  advancedSearchPath,
  onAdvancedSearchClick,
}) => {
  const { t } = useTranslation(['header']);
  const [showEmptySearchAlert, setShowEmptySearchAlert] = useState(false);

  const handleOptionClick = (value) => {
    if (!searchValue?.trim()) {
      setShowEmptySearchAlert(true);
      return;
    }
    setShowEmptySearchAlert(false);
    onSelectOption(value);
    onHide();
    setTimeout(() => {
      if (onSubmit) {
        onSubmit(value);
      }
    }, 200);
  };

  const handleHide = () => {
    setShowEmptySearchAlert(false);
    onHide();
  };

  const searchOptions = [
    {
      id: 'titulo',
      label: t('title'),
      description: t('searchByTitle'),
      icon: <BsBook size={32} />,
      value: 'titulo'
    },
    {
      id: 'autor',
      label: t('author'),
      description: t('searchByAuthor'),
      icon: <BsPerson size={32} />,
      value: 'autor'
    },
    {
      id: 'editorial',
      label: t('editorial'),
      description: t('searchByEditorial'),
      icon: <BsBuilding size={32} />,
      value: 'editorial'
    },
    {
      id: 'ean',
      label: 'ISBN',
      description: t('searchByISBN'),
      icon: <BsHash size={32} />,
      value: 'ean'
    }
  ];

  return (
    <Modal 
      show={show} 
      onHide={handleHide} 
      centered 
      className="search-options-modal"
      backdrop={true}
    >
      <Modal.Header className="search-modal-header">
        <Modal.Title className="search-modal-title">
          {t('whatDoYouWantToSearch')}
        </Modal.Title>
        <button 
          type="button" 
          className="search-modal-close" 
          onClick={handleHide}
          aria-label="Cerrar"
        >
          <FaXmark size={20} />
        </button>
      </Modal.Header>
      <Modal.Body className="search-modal-body">
        {showEmptySearchAlert && (
          <Alert variant="warning" className="mb-3">
            {t('fillSearchFieldToSearch')}
          </Alert>
        )}
        {searchValue && (
          <div className="search-modal-current">
            {t('searching')}: "{searchValue}"
          </div>
        )}
        <Row className="search-options-grid">
          {searchOptions.map((option) => (
            <Col 
              xs={6} 
              key={option.id}
              className="search-option-col"
            >
              <div
                className={`search-option-card ${
                  currentSearchType === option.value ? 'selected' : ''
                }`}
                onClick={() => handleOptionClick(option.value)}
              >
                <div className="search-option-icon">
                  {option.icon}
                </div>
                <div className="search-option-label">
                  {option.label}
                </div>
                <div className="search-option-description">
                  {option.description}
                </div>
              </div>
            </Col>
          ))}
        </Row>
        <div className="search-modal-advanced">
          <Link 
            to={advancedSearchPath} 
            className="search-advanced-link"
            onClick={() => {
              if (onAdvancedSearchClick) {
                onAdvancedSearchClick();
              }
              handleHide();
            }}
          >
            {t('advancedSearch')}
          </Link>
        </div>
      </Modal.Body>
    </Modal>
  );
};

SearchOptionsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  currentSearchType: PropTypes.string,
  onSelectOption: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  advancedSearchPath: PropTypes.string.isRequired,
  onAdvancedSearchClick: PropTypes.func,
};

const SearchBarDesktop = ({ isDesktop, secondaryImage = false }) => {
  const { t } = useTranslation(['header']);
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const { libraryPath } = useAssignedLibrary();

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    const currentPathname = location.pathname;
    
    if (showModal && 
        !prevPathname.includes('/lista/search') && 
        currentPathname.includes('/lista/search')) {
      setShowModal(false);
    }
    
    prevPathnameRef.current = currentPathname;
  }, [location.pathname, showModal]);

  const handleSubmitCustom = (values) => {
    if (values.value && values.value.trim()) {
      setShowModal(true);
    }
  };

  return (
    <Col lg={!secondaryImage ? 5 : 5} className="header__search desktop ">
      <div className="search-desktop " data-testid="search-desktop">
        <SearchBar
          isDesktop={isDesktop}
          initialValues={initialSearchValues}
          onSubmitCustom={handleSubmitCustom}
        >
          {({ values, setFieldValue, handleDirectSubmit }) => (
            <>
              <Field name="searchType" type="hidden" />
              <InputGroup.Text>
                <Button 
                  type="button" 
                  className="deskt-search "
                  onClick={(e) => {
                    e.preventDefault();
                    setShowModal(true);
                  }}
                >
                  {t('search')} 
                </Button>
              </InputGroup.Text>
              <SearchOptionsModal 
                show={showModal} 
                onHide={() => {
                  setShowModal(false);
                }}
                searchValue={values.value}
                currentSearchType={values.searchType}
                onSelectOption={(value) => {
                  setFieldValue('searchType', value);
                }}
                onSubmit={(selectedSearchType) => handleDirectSubmit(selectedSearchType)}
                advancedSearchPath={libraryPath('lista/search')}
                onAdvancedSearchClick={() => {
                  setFieldValue('value', '');
                }}
              />
            </>
          )}
        </SearchBar>
      </div>
    </Col>
  );
}

SearchBarDesktop.propTypes = {
  isDesktop: PropTypes.bool,
  secondaryImage: PropTypes.bool
};

export default SearchBarDesktop;

